import React from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import TrustBar from '../components/TrustBar';
import { useLanguage, useTranslation } from '../i18n/LanguageProvider';
import { translations } from '../i18n/translations';

const HomePage: React.FC = () => {
  const { language } = useLanguage();
  const { t } = useTranslation();

  const services = [
    {
      title: t('services.cards.s1Title'),
      description: t('services.cards.s1Desc'),
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      title: t('services.cards.s2Title'),
      description: t('services.cards.s2Desc'),
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      )
    },
    {
      title: t('services.cards.s3Title'),
      description: t('services.cards.s3Desc'),
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
        </svg>
      )
    },
    {
      title: t('services.cards.s4Title'),
      description: t('services.cards.s4Desc'),
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      )
    }
  ];

  const dict = translations[language];
  const featuredCases = [
    {
      title: dict.featured.cases.c1Title,
      client: dict.featured.cases.c1Client,
      description: dict.featured.cases.c1Desc,
      tags: dict.featured.cases.c1Tags
    },
    {
      title: dict.featured.cases.c2Title,
      client: dict.featured.cases.c2Client,
      description: dict.featured.cases.c2Desc,
      tags: dict.featured.cases.c2Tags
    },
    {
      title: dict.featured.cases.c3Title,
      client: dict.featured.cases.c3Client,
      description: dict.featured.cases.c3Desc,
      tags: dict.featured.cases.c3Tags
    }
  ];

  return (
    <div>
      <Hero />
      <TrustBar />
      
      {/* Services Overview */}
      <section id="services" className="py-16 md:py-24 bg-gradient-to-b from-white to-sand/30 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-20 w-32 h-32 border border-navy/20 rounded-full"></div>
          <div className="absolute bottom-20 right-20 w-24 h-24 border border-sky/30 rotate-45"></div>
          <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-sky/10 rounded-full"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-sky/10 rounded-full text-navy text-sm font-medium mb-4 animate-fade-in-up">
              {t('services.badge')}
            </div>
            <h2 className="text-4xl md:text-6xl font-bold text-navy mb-6 font-space animate-fade-in-up animation-delay-200">
              {t('services.title')}
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto animate-fade-in-up animation-delay-400">
              {t('services.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className="text-center group relative">
                {/* Card Background with Glassmorphism */}
                <div className="absolute inset-0 bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:bg-white/80"></div>
                
                <div className="relative p-8 z-10">
                  <div className="flex justify-center mb-6">
                    <div className="p-4 bg-gradient-to-br from-sky/20 to-sky/10 rounded-2xl text-navy group-hover:from-sky/30 group-hover:to-sky/20 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3">
                      {service.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-4 font-space group-hover:text-sky transition-colors duration-200">
                    {service.title}
                  </h3>
                  <p className="text-slate leading-relaxed">
                    {service.description}
                  </p>
                </div>
                
                {/* Hover Glow Effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-sky/0 via-sky/5 to-sky/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link 
              to="/services"
              className="bg-navy text-sand px-8 py-4 rounded-lg font-semibold hover:bg-navy/90 transition-colors duration-200 inline-block"
            >
              {t('services.viewAll')}
            </Link>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-16 left-10 w-24 h-24 border border-sky/20 rounded-xl rotate-12"></div>
          <div className="absolute bottom-16 right-10 w-20 h-20 border border-navy/20 rounded-full"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-navy/10 rounded-full text-navy text-sm font-medium mb-4">
              {t('processHome.badge')}
            </div>
            <h2 className="text-4xl md:text-6xl font-bold text-navy mb-4 font-space">
              {t('processHome.title')}
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              {t('processHome.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[1,2,3,4,5].map((step) => (
              <div key={step} className="bg-sand rounded-xl p-6 shadow-sm">
                <div className="text-sky font-space font-bold text-sm mb-2">{String(step).padStart(2, '0')}</div>
                <h3 className="text-lg font-bold text-navy mb-2">
                  {t(`processHome.steps.s${step}.title` as any)}
                </h3>
                <p className="text-slate">
                  {t(`processHome.steps.s${step}.desc` as any)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Case Studies */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-sand via-sand/80 to-sky/10 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-64 h-64 bg-sky/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 left-10 w-48 h-48 bg-navy/5 rounded-full blur-2xl animate-pulse animation-delay-2000"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-navy/10 rounded-full text-navy text-sm font-medium mb-4 animate-fade-in-up">
              {t('featured.badge')}
            </div>
            <h2 className="text-4xl md:text-6xl font-bold text-navy mb-6 font-space animate-fade-in-up animation-delay-200">
              {t('featured.title')}
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto animate-fade-in-up animation-delay-400">
              {t('featured.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredCases.map((caseStudy, index) => (
              <Link key={index} to={`/case-studies#case-study-${index + 1}`} className="group relative block">
                {/* Card with Modern Design */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-2 border border-white/50 relative overflow-hidden">
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-sky/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                  
                  <div className="relative z-10">
                    <div className="flex flex-wrap gap-2 mb-6">
                      {caseStudy.tags.map((tag, tagIndex) => (
                        <span key={tagIndex} className="px-3 py-1 bg-gradient-to-r from-sky/20 to-sky/10 text-navy text-sm rounded-full font-medium border border-sky/20 group-hover:from-sky/30 group-hover:to-sky/20 transition-all duration-200">
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    <h3 className="text-xl font-bold text-navy mb-2 font-space group-hover:text-sky transition-colors duration-200">
                      {caseStudy.title}
                    </h3>
                    <p className="text-slate font-medium mb-4 opacity-80">{caseStudy.client}</p>
                    <p className="text-slate mb-6 leading-relaxed">{caseStudy.description}</p>
                    
                    {/* Read More Arrow */}
                    <div className="flex items-center text-sky font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                      <span className="text-sm mr-2">{t('featured.learnMore')}</span>
                      <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </div>
                  </div>
                  
                  {/* Corner Accent */}
                  <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-sky/20 to-transparent rounded-2xl"></div>
                </div>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link 
              to="/case-studies"
              className="bg-sky text-navy px-8 py-4 rounded-lg font-semibold hover:bg-sky/90 transition-colors duration-200 inline-block"
            >
              {t('featured.viewAll')}
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-16 md:py-24 bg-gradient-to-r from-navy via-slate-800 to-navy relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJtIDYwIDAgTCAwIDAgMCA2MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDEzNSwgMjA2LCAyMzUsIDAuMDUpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
          <div className="absolute top-20 right-20 w-72 h-72 bg-sky/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-sand/10 rounded-full blur-2xl animate-pulse animation-delay-2000"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block px-4 py-2 bg-sky/10 rounded-full text-sand text-sm font-medium mb-6 animate-fade-in-up">
              {t('cta.badge')}
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sand via-sky to-sand mb-8 font-space animate-fade-in-up animation-delay-200">
              {t('cta.title')}
            </h2>
            
            <p className="text-xl text-sand/80 mb-12 max-w-3xl mx-auto leading-relaxed animate-fade-in-up animation-delay-400">
              {t('cta.subtitleP1')}
              <span className="text-sky font-semibold">{t('cta.subtitleP2')}</span>
              {language === 'en' ? ' while keeping your ' : ' terwijl je '}
              <span className="text-sky font-semibold">{t('cta.subtitleP3')}</span>
              {language === 'en' ? '.' : ' blijft.'}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center animate-fade-in-up animation-delay-600">
              <Link 
                to="/contact#calendly"
                className="group bg-gradient-to-r from-sky to-sky/80 text-navy px-10 py-5 rounded-full font-semibold text-lg hover:shadow-2xl hover:shadow-sky/25 transition-all duration-300 transform hover:-translate-y-1 inline-flex items-center gap-2"
              >
                <span>{t('cta.ctaPrimary')}</span>
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </Link>
              
              <a 
                href="mailto:hello@yourcompany.com"
                className="text-sand/90 font-semibold text-lg hover:text-sky transition-colors duration-200 flex items-center gap-2 group"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>{t('cta.ctaSecondary')}</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
